import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Switch,
  VStack,
  HStack,
  useToast,
  Box,
  Text,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  FormHelperText,
  Badge,
  Code,
} from '@chakra-ui/react';
import { useApi } from '@/contexts/ApiContext';

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TemplateFormData {
  name: string;
  key: string;
  type: string;
  subject: string;
  body: string;
  contentType: string;
  variables: string[];
  isActive: boolean;
  tags: string[];
  metadata: Record<string, any>;
}

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const api = useApi();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [newVariable, setNewVariable] = useState('');

  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    key: '',
    type: 'email',
    subject: '',
    body: '',
    contentType: 'text/html',
    variables: [],
    isActive: true,
    tags: [],
    metadata: {},
  });

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      if (!formData.name.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Template name is required',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (!formData.key.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Template key is required',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (!formData.body.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Template body is required',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Auto-detect variables in the template body
      const detectedVariables = extractVariables(formData.body);
      const allVariables = [...new Set([...formData.variables, ...detectedVariables])];

      await api.NotificationsApi.createTemplate({
        template: {
          name: formData.name,
          body_template: formData.body,
          subject_template: formData.subject,
          language: 'en',
          required_variables: allVariables,
        }
      });

      toast({
        title: 'Success',
        description: 'Template created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Failed to create template:', error);
      toast({
        title: 'Error',
        description: 'Failed to create template',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      key: '',
      type: 'email',
      subject: '',
      body: '',
      contentType: 'text/html',
      variables: [],
      isActive: true,
      tags: [],
      metadata: {},
    });
    setNewTag('');
    setNewVariable('');
    onClose();
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddVariable = () => {
    if (newVariable.trim() && !formData.variables.includes(newVariable.trim())) {
      setFormData(prev => ({
        ...prev,
        variables: [...prev.variables, newVariable.trim()]
      }));
      setNewVariable('');
    }
  };

  const handleRemoveVariable = (variableToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter(variable => variable !== variableToRemove)
    }));
  };

  const extractVariables = (text: string): string[] => {
    const regex = /\{\{(\w+)\}\}/g;
    const variables: string[] = [];
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }
    
    return variables;
  };

  const generateKeyFromName = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      key: prev.key || generateKeyFromName(name)
    }));
  };

  const detectedVariables = extractVariables(formData.body + (formData.subject || ''));

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Notification Template</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Basic Information */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3}>
                Basic Information
              </Text>
              <VStack spacing={3}>
                <FormControl isRequired>
                  <FormLabel>Template Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g., Welcome Email"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Template Key</FormLabel>
                  <Input
                    value={formData.key}
                    onChange={(e) => setFormData(prev => ({ ...prev, key: e.target.value }))}
                    placeholder="e.g., welcome_email"
                    fontFamily="mono"
                  />
                  <FormHelperText>
                    Unique identifier for this template (used in code)
                  </FormHelperText>
                </FormControl>

                <HStack spacing={4} width="100%">
                  <FormControl isRequired>
                    <FormLabel>Template Type</FormLabel>
                    <Select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option value="email">Email</option>
                      <option value="telegram">Telegram</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="sms">SMS</option>
                      <option value="push">Push Notification</option>
                      <option value="in_app">In-App</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Content Type</FormLabel>
                    <Select
                      value={formData.contentType}
                      onChange={(e) => setFormData(prev => ({ ...prev, contentType: e.target.value }))}
                    >
                      <option value="text/html">HTML</option>
                      <option value="text/plain">Plain Text</option>
                      <option value="markdown">Markdown</option>
                    </Select>
                  </FormControl>
                </HStack>

                <FormControl>
                  <FormLabel>Active</FormLabel>
                  <Switch
                    isChecked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    colorScheme="primary"
                  />
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* Content */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3}>
                Template Content
              </Text>
              <VStack spacing={3}>
                {(formData.type === 'email' || formData.type === 'push') && (
                  <FormControl>
                    <FormLabel>Subject</FormLabel>
                    <Input
                      value={formData.subject}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="e.g., Welcome to our platform, {{userName}}!"
                    />
                  </FormControl>
                )}

                <FormControl isRequired>
                  <FormLabel>Body</FormLabel>
                  <Textarea
                    value={formData.body}
                    onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                    placeholder="Template content with {{variables}}"
                    minHeight="200px"
                  />
                  <FormHelperText>
                    Use double curly braces for variables: {"{{variableName}}"}
                  </FormHelperText>
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* Variables */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3}>
                Variables
              </Text>
              
              {detectedVariables.length > 0 && (
                <Box mb={3}>
                  <Text fontSize="sm" color="green.600" mb={2}>
                    Auto-detected variables:
                  </Text>
                  <Wrap>
                    {detectedVariables.map((variable) => (
                      <WrapItem key={variable}>
                        <Badge colorScheme="green" variant="subtle">
                          {variable}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              )}

              <VStack spacing={3}>
                <FormControl>
                  <FormLabel>Manual Variables</FormLabel>
                  <HStack>
                    <Input
                      value={newVariable}
                      onChange={(e) => setNewVariable(e.target.value)}
                      placeholder="Add variable name"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddVariable()}
                    />
                    <Button onClick={handleAddVariable} size="sm">
                      Add
                    </Button>
                  </HStack>
                  {formData.variables.length > 0 && (
                    <Wrap mt={2}>
                      {formData.variables.map((variable) => (
                        <WrapItem key={variable}>
                          <Tag size="md" variant="solid" colorScheme="blue">
                            <TagLabel>{variable}</TagLabel>
                            <TagCloseButton onClick={() => handleRemoveVariable(variable)} />
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* Tags */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3}>
                Tags
              </Text>
              <FormControl>
                <HStack>
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  />
                  <Button onClick={handleAddTag} size="sm">
                    Add
                  </Button>
                </HStack>
                {formData.tags.length > 0 && (
                  <Wrap mt={2}>
                    {formData.tags.map((tag) => (
                      <WrapItem key={tag}>
                        <Tag size="md" variant="solid" colorScheme="primary">
                          <TagLabel>{tag}</TagLabel>
                          <TagCloseButton onClick={() => handleRemoveTag(tag)} />
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}
              </FormControl>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Creating..."
          >
            Create Template
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};